/**
 * Which Rudi connection this browser session talks to.
 *
 * The Cortex backend serves its server-configured connection under `/api` and
 * every other registered connection (development stacks) under
 * `/api/targets/<id>`. The choice is fixed for a page load: the API client and
 * the auth client read their base URL once, so switching reloads. Each
 * connection keeps its own stored session (see auth-storage), so switching
 * back to a connection you signed in to earlier does not ask for a password.
 */
export const TARGET_KEY = 'cortex_target';
export const TARGET_LABEL_KEY = 'cortex_target_label';
export const TARGET_QUERY = 'target';
const TARGET_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

export interface TargetSummary {
  id: string;
  label: string;
  kind: 'standing' | 'stack';
  frontendUrl?: string;
  branch?: string;
  worktree?: string;
  startedAt?: string;
  test?: boolean;
}

function read(key: string): string {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function write(key: string, value: string): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Ignore storage errors.
  }
}

/** Selected connection id; '' means the server-configured connection. */
export function readTargetId(): string {
  if (typeof window === 'undefined') return '';
  const requested = new URLSearchParams(window.location.search).get(TARGET_QUERY);
  if (requested !== null) {
    const id = TARGET_ID.test(requested) ? requested : '';
    if (id !== read(TARGET_KEY)) {
      write(TARGET_KEY, id);
      write(TARGET_LABEL_KEY, '');
    }
    return id;
  }
  const stored = read(TARGET_KEY);
  return TARGET_ID.test(stored) ? stored : '';
}

export function readTargetLabel(): string {
  return read(TARGET_LABEL_KEY);
}

export function apiBasePath(targetId: string): string {
  return targetId ? `/api/targets/${encodeURIComponent(targetId)}` : '/api';
}

/** The host of a stack's frontend, which carries its unique stack prefix. */
export function targetHost(target: Pick<TargetSummary, 'frontendUrl'>): string {
  if (!target.frontendUrl) return '';
  try {
    return new URL(target.frontendUrl).host;
  } catch {
    return '';
  }
}

function startedAgo(value: string, now: number): string {
  const minutes = Math.round((now - Date.parse(value)) / 60_000);
  if (Number.isNaN(minutes)) return '';
  const format = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (Math.abs(minutes) < 60) return `started ${format.format(-minutes, 'minute')}`;
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 48) return `started ${format.format(-hours, 'hour')}`;
  return `started ${format.format(-Math.round(hours / 24), 'day')}`;
}

/** Second line of a picker entry: the parts that tell similar stacks apart. */
export function targetDetails(target: TargetSummary, now: number = Date.now()): string {
  if (target.kind === 'standing') return 'Server-configured Rudi';
  return [
    targetHost(target),
    target.worktree && `worktree ${target.worktree}`,
    target.startedAt && startedAgo(target.startedAt, now),
    target.test && 'test copy',
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Persist a connection choice for the next page load and reload there.
 *
 * The current section survives the switch; deeper paths hold record IDs that
 * belong to the old connection, so only the first path segment is kept.
 */
export function switchTarget(target: TargetSummary | null): void {
  write(TARGET_KEY, target && target.kind !== 'standing' ? target.id : '');
  write(TARGET_LABEL_KEY, target && target.kind !== 'standing' ? target.label : '');
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const section = url.pathname.split('/').filter(Boolean)[0];
  url.pathname = section ? `/${section}` : '/';
  url.search = '';
  url.hash = '';
  window.location.assign(url.toString());
}

export async function fetchTargets(fetcher: typeof fetch = fetch): Promise<TargetSummary[]> {
  const response = await fetcher('/api/targets', { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Rudi connections unavailable (${response.status})`);
  const data = (await response.json()) as { targets?: unknown };
  if (!Array.isArray(data.targets)) return [];
  return data.targets.filter(
    (item): item is TargetSummary =>
      !!item &&
      typeof item === 'object' &&
      typeof (item as TargetSummary).id === 'string' &&
      typeof (item as TargetSummary).label === 'string'
  );
}
